/**
 * The 30-day due-date strip — the second signature element.
 *
 * Two joined halves. The rail on top is a true scale: today at the left, week
 * ticks across it, and a dot wherever money is due, so the shape of the month
 * is visible in one glance. Beneath it the same days become readable cards, in
 * order, grouped by day — because forty accounts routinely put four payments on
 * the same date and proportional cards would sit on top of each other.
 *
 * Overdue items collapse into one rose column pinned to the left edge. They are
 * not "day -3"; they are late, and that is the only thing worth saying.
 */
import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangleIcon } from 'lucide-react'
import {
  daysFromToday,
  formatDay,
  formatDueDate,
  formatMoney,
  type ApiDate,
  type Decimal,
} from '@/lib/format'
import { cn } from '@/lib/utils'
import { Money } from './Money'

export interface DueDateItem {
  id: string
  name: string
  dueDate: ApiDate
  amount?: Decimal
  currency?: string
  /** Where clicking the card goes. Omit for a non-interactive strip. */
  href?: string
  /** Paid obligations stay on the strip but read as settled, not as a task. */
  paid?: boolean
}

export interface DueDateTimelineProps {
  items: DueDateItem[]
  /** How far ahead the strip runs. 30 days is the household's planning horizon. */
  days?: number
  today?: Date
  /** Shown when nothing is due in the window. */
  empty?: ReactNode
  className?: string
}

interface DayGroup {
  /** Days from today; -1 is the collapsed overdue bucket. */
  offset: number
  label: string
  items: DueDateItem[]
  overdue: boolean
  urgency: 'paid' | 'overdue' | 'urgent' | 'soon' | 'later'
}

export function DueDateTimeline({
  items,
  days = 30,
  today = new Date(),
  empty,
  className,
}: DueDateTimelineProps) {
  const groups = groupByDay(items, days, today)

  if (groups.length === 0) {
    return (
      <div
        className={cn(
          'border-hairline text-dim rounded-lg border border-dashed px-4 py-8 text-center text-sm',
          className,
        )}
      >
        {empty ?? `Nothing due in the next ${days} days. Next statement close will fill this in.`}
      </div>
    )
  }

  return (
    <div className={cn('min-w-0', className)}>
      {/* The scale. Proportional, and the only part that is. */}
      <div className="relative h-8" aria-hidden>
        <div className="bg-hairline absolute top-6 right-0 left-0 h-px" />

        {weekTicks(days).map((tick) => (
          <Fragment key={tick.offset}>
            <span
              className="bg-hairline absolute top-4 h-2 w-px"
              style={{ left: `${(tick.offset / days) * 100}%` }}
            />
            <span
              className="eyebrow absolute top-0 -translate-x-1/2"
              style={{ left: `${(tick.offset / days) * 100}%` }}
            >
              +{tick.offset}d
            </span>
          </Fragment>
        ))}

        <span className="bg-cyan absolute top-3 bottom-0 left-0 w-0.5" />
        <span className="eyebrow text-cyan absolute top-0 left-0">Today</span>

        {groups.map((group) => (
          <span
            key={group.offset}
            className={cn(
              'absolute top-[1.3125rem] size-2 rounded-full',
              // Overdue sits just right of the today line rather than under it.
              group.overdue ? 'translate-x-1' : '-translate-x-1/2',
              DOT[group.urgency],
            )}
            style={{ left: `${(Math.max(group.offset, 0) / days) * 100}%` }}
          />
        ))}
      </div>

      {/* The readable half. Scrolls horizontally rather than shrinking cards
          below legibility on a phone. */}
      <div className="-mx-1 overflow-x-auto px-1 pt-3 pb-1">
        <ol className="flex list-none items-start gap-3">
          {groups.map((group) => (
            <li key={group.offset} className="w-36 shrink-0">
              <div
                className={cn(
                  'eyebrow mb-2 flex items-center gap-1',
                  group.overdue && 'text-rose',
                  group.offset === 0 && !group.overdue && 'text-cyan',
                )}
              >
                {group.overdue && <AlertTriangleIcon className="size-3" aria-hidden />}
                {group.label}
              </div>
              <div className="flex flex-col gap-1.5">
                {group.items.map((item) => (
                  <DueCard key={item.id} item={item} today={today} />
                ))}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

const DOT: Record<DayGroup['urgency'], string> = {
  paid: 'bg-mint',
  overdue: 'bg-rose',
  urgent: 'bg-rose',
  soon: 'bg-amber',
  later: 'bg-cyan',
}

function DueCard({ item, today }: { item: DueDateItem; today: Date }) {
  const days = daysFromToday(item.dueDate, today)
  const urgent = !item.paid && days !== null && days <= 2
  const soon = !item.paid && days !== null && days > 2 && days <= 7

  const label = `${item.name}. ${
    item.paid ? 'Paid.' : `${formatDueDate(item.dueDate, today)}.`
  } ${formatMoney(item.amount, { currency: item.currency })}`

  const className = cn(
    'border-hairline bg-panel flex w-full flex-col gap-1 rounded-md border px-2 py-1.5 text-left',
    'hover:bg-panel-2 focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2',
    urgent && 'border-rose/50',
    soon && 'border-amber/40',
    item.paid && 'opacity-70',
  )

  const body = (
    <>
      <span className="truncate text-xs font-medium">{item.name}</span>
      <span className="flex items-baseline justify-between gap-2">
        <Money
          value={item.amount}
          currency={item.currency}
          size="xs"
          tone={item.paid ? 'dim' : 'neutral'}
        />
        <span
          className={cn(
            'tabular text-[0.625rem]',
            item.paid ? 'text-mint' : urgent ? 'text-rose' : soon ? 'text-amber' : 'text-dim',
          )}
        >
          {item.paid
            ? 'Paid'
            : days === null
              ? '—'
              : days < 0
                ? `${Math.abs(days)}d late`
                : `${days}d`}
        </span>
      </span>
    </>
  )

  if (!item.href) {
    return (
      <div className={className} aria-label={label}>
        {body}
      </div>
    )
  }

  return (
    <Link to={item.href} className={className} aria-label={label}>
      {body}
    </Link>
  )
}

function groupByDay(items: DueDateItem[], days: number, today: Date): DayGroup[] {
  const buckets = new Map<number, DayGroup>()

  for (const item of items) {
    const offset = daysFromToday(item.dueDate, today)
    if (offset === null || offset > days) continue

    const key = offset < 0 ? -1 : offset
    let bucket = buckets.get(key)
    if (!bucket) {
      bucket = {
        offset: key,
        label: key < 0 ? 'Overdue' : key === 0 ? 'Today' : formatDay(item.dueDate),
        items: [],
        overdue: key < 0,
        urgency: key < 0 ? 'overdue' : key <= 2 ? 'urgent' : key <= 7 ? 'soon' : 'later',
      }
      buckets.set(key, bucket)
    }
    bucket.items.push(item)
  }

  // A day everyone has already paid is no longer a warning.
  for (const bucket of buckets.values()) {
    if (bucket.items.every((item) => item.paid)) bucket.urgency = 'paid'
  }

  return [...buckets.values()].sort((a, b) => a.offset - b.offset)
}

function weekTicks(days: number): { offset: number }[] {
  const ticks: { offset: number }[] = []
  for (let offset = 7; offset < days; offset += 7) ticks.push({ offset })
  return ticks
}
